import { Option, Result } from '@sapphire/result';

import {
  LaunchSameSettings,
  MC_MESSAGES,
  McAddedLocalePricing,
  McInheritedLaunch,
  mcTraditionalTitle,
  McTraditionalSurveyPayloadInput,
  parseSurveyLocalization,
  expectedEntryLinks,
  Survey,
  SurveyLaunchForm,
  SurveyLocaleData,
  TraditionalMcLocale,
} from '@ps/survey-utils';

import { firstChildEntry } from './audience-children.helper';
import {
  preDraftChildDraft,
  PreDraftChildDraftsInput,
} from './pre-draft-save.helper';
import { SurveyChildEntry } from '../../survey-creation-child-registry.helper';

export type McTraditionalSurveyOutcome =
  | {
      readonly status: 'created';
      readonly locale: string;
      readonly isParent: boolean;
      readonly survey: Survey;
    }
  | { readonly status: 'rejected'; readonly locale: string };

export type McCreatedSurvey = Extract<
  McTraditionalSurveyOutcome,
  { status: 'created' }
>;

export function mcParentSurvey(created: readonly McCreatedSurvey[]): Survey {
  return (created.find(({ isParent }) => isParent) ?? created[0]).survey;
}

// Country comes from the child key, not the created survey: a create response
// without a `locale` block maps to an empty country code, grouping everything.
export function sameCountrySiblingIds(
  created: readonly McCreatedSurvey[],
  current: McCreatedSurvey,
): number[] {
  const { countryCode } = parseSurveyLocalization(current.locale);

  return created
    .filter(
      (candidate) =>
        candidate.survey.id !== current.survey.id &&
        parseSurveyLocalization(candidate.locale).countryCode === countryCode,
    )
    .map((candidate) => candidate.survey.id);
}

// Same rule as `sameCountrySiblingIds`, but these children carry a saved
// survey, so the country is read off it directly.
export function sameCountrySurveyIds(
  children: readonly SurveyChildEntry[],
  added: Survey,
): number[] {
  return children.flatMap(([, , { survey }]) =>
    Option.from(survey)
      .filter(
        ({ id, countryCode }) =>
          id !== added.id && countryCode === added.countryCode,
      )
      .mapOr<number[], number[]>([], ({ id }) => [id]),
  );
}

/** The launch page's state when a locale is added from its drawer. */
export interface LaunchPageInheritance {
  readonly launch: SurveyLaunchForm;
  readonly sameSettings: LaunchSameSettings;
}

export function addedLocaleInput(
  source: SurveyChildEntry,
  context: PreDraftChildDraftsInput,
  locale: SurveyLocaleData,
  launchPage: Option<LaunchPageInheritance> = Option.none,
  addedLocale: McAddedLocalePricing = { keepsOfferPrice: false },
): Result<McTraditionalSurveyPayloadInput, string> {
  const [, , { survey }] = source;

  return Option.from(survey)
    .andThen((sourceSurvey) =>
      Option.from(sourceSurvey.mcUniqueId).map(
        (mcUniqueId) => [sourceSurvey, mcUniqueId] as const,
      ),
    )
    .match({
      some: ([sourceSurvey, mcUniqueId]) =>
        preDraftChildDraft(source, context)
          .map(
            ({ input }): McTraditionalSurveyPayloadInput => ({
              ...input,
              metadata: {
                ...input.metadata,
                selectedCountries: [locale.countryCode],
                selectedLanguages: [locale.languageShortCode],
              },
              mcUniqueId,
              isParent: false,
              addedLocale,
            }),
          )
          .andThen((input) =>
            launchPage.match({
              some: (page) =>
                uniqueLinksCoverAddedLocale(
                  context.children,
                  sourceSurvey,
                  page.launch,
                ).map(() => ({
                  ...input,
                  inheritedLaunch: inheritedLaunchOf(
                    sourceSurvey,
                    page.sameSettings,
                  ),
                })),
              none: () => Result.ok(input),
            }),
          ),
      none: () => Result.err(context.invalidChildMessage),
    });
}

/** The group as it stands once the replaced locale is gone. */
export function withoutChildSurvey(
  context: PreDraftChildDraftsInput,
  replaced: Option<number>,
): PreDraftChildDraftsInput {
  return replaced.mapOr(context, (surveyId) => ({
    ...context,
    children: context.children.filter(
      ([, , { survey }]) => survey?.id !== surveyId,
    ),
  }));
}

// Base title and group id come from the group's first survey once the replaced
// one is gone; a group of one falls back to the replaced survey.
export function traditionalMcLocaleOf(
  context: PreDraftChildDraftsInput,
  replaced: Option<number>,
  localization: string,
  { keepsOfferPrice }: McAddedLocalePricing,
): Result<TraditionalMcLocale, string> {
  const { countryCode, languageShortCode } =
    parseSurveyLocalization(localization);

  return firstChildEntry(withoutChildSurvey(context, replaced).children)
    .or(firstChildEntry(context.children))
    .andThen(([, , { survey }]) => Option.from(survey))
    .andThen((source) =>
      Option.from(source.mcUniqueId).map(
        (mcUniqueId): TraditionalMcLocale => ({
          mcUniqueId,
          title: mcTraditionalTitle(
            source.title,
            countryCode,
            languageShortCode,
          ),
          offerPrice: Option.from(context.pricing.offerPrice).filter(
            () => keepsOfferPrice,
          ),
        }),
      ),
    )
    .okOr(context.invalidChildMessage);
}

// Legacy: traditional-view new-locale request — row[0]'s unique links always,
// its URLs only under "same Link".
export function inheritedLaunchOf(
  source: Survey,
  sameSettings: LaunchSameSettings,
): McInheritedLaunch {
  return {
    uniqueLinks: source.uniqueLinks ?? null,
    liveUrl: sameSettings.surveyUrls ? (source.liveUrl ?? '') : '',
    testUrl: sameSettings.surveyUrls ? (source.testUrl ?? '') : '',
  };
}

// Legacy: with "same File Links", the uploaded links must cover every locale
// plus the new one, which counts as the first locale again.
export function uniqueLinksCoverAddedLocale(
  children: readonly SurveyChildEntry[],
  first: Survey,
  launch: SurveyLaunchForm,
): Result<void, string> {
  const { sameSettingsForAllSurveys, entryLinks } = launch.uniqueLinks;
  if (!sameSettingsForAllSurveys || !entryLinks.length) {
    return Result.ok(undefined);
  }
  const surveys = [
    ...children.flatMap(([, , { survey }]) => (survey ? [survey] : [])),
    first,
  ];
  const expectedLinks = expectedEntryLinks(surveys);
  return entryLinks.length < expectedLinks
    ? Result.err(
        MC_MESSAGES.UNIQUE_LINKS_BELOW_EXPECTED.replace(
          '<expectedLinks>',
          String(expectedLinks),
        ),
      )
    : Result.ok(undefined);
}
